import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { systemConfigService } from 'services/systemConfigService';
import { parseApiError } from 'utils/errorParser';
import { notify } from 'features/ui/uiSlice';
import { selectCurrentUser } from 'features/auth/authSelectors';
import { hasPermission } from 'utils/permissions';
import { MODULES, ACTIONS } from 'constants/permissions';

export const fetchSystemConfig = createAsyncThunk('systemConfig/fetch', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const user = selectCurrentUser(state);

  if (!hasPermission(user, MODULES.SYSTEM_CONFIG, ACTIONS.VIEW)) {
    // If the user doesn't have permission to view the system config, 
    // skip the API call to avoid triggering a global 403 error toast.
    // Return a safe fallback config instead.
    return { articleHistoryDays: 40 };
  }

  try {
    return await systemConfigService.get();
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const saveSystemConfig = createAsyncThunk('systemConfig/save', async (model, { dispatch, rejectWithValue }) => {
  try {
    const saved = await systemConfigService.upsert(model);
    dispatch(notify({ type: 'success', message: 'System Configuration saved.' }));
    return saved;
  } catch (error) {
    const parsed = parseApiError(error);
    dispatch(notify({ type: 'error', message: parsed.message }));
    return rejectWithValue(parsed);
  }
});

const systemConfigSlice = createSlice({
  name: 'systemConfig',
  initialState: {
    config: null,
    status: 'idle',
    saving: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemConfig.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchSystemConfig.fulfilled, (state, action) => { state.status = 'succeeded'; state.config = action.payload; })
      .addCase(fetchSystemConfig.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload?.message; })
      .addCase(saveSystemConfig.pending, (state) => { state.saving = true; })
      .addCase(saveSystemConfig.fulfilled, (state, action) => { state.saving = false; state.config = action.payload; })
      .addCase(saveSystemConfig.rejected, (state) => { state.saving = false; });
  },
});

export default systemConfigSlice.reducer;
